import * as React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import { useMergeRefs } from "@chakra-ui/react";
import { useDivideControlProps } from "./hooks";
import {
  ImagePicker,
  ImagePickerActions,
  ImagePickerContent,
  ImagePickerProps,
} from "../image-picker";

const omittedProps = ["name"] as const;
type OmittedImagePickerProps = Omit<
  ImagePickerProps,
  typeof omittedProps[number]
>;

type FormImagePickerProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> & OmittedImagePickerProps;

function FormImagePickerInner<TFieldValues extends FieldValues>(
  props: FormImagePickerProps<TFieldValues>,
  ref?: React.ForwardedRef<HTMLInputElement>
) {
  const {
    setValue: setValueProp,
    onDelete: onDeleteProp,
    ...restProps
  } = props;
  const [imagePickerProps, controllerProps] = useDivideControlProps<
    TFieldValues,
    FormImagePickerProps<TFieldValues>
  >(restProps);

  const {
    field: { ref: fieldRef, onChange, ...fieldProps },
  } = useController(controllerProps);

  const mergedRef = useMergeRefs(ref, fieldRef);

  const handleChange: ImagePickerProps["setValue"] = (file) => {
    setValueProp?.(file);
    onChange(file);
  };

  const handleDelete: ImagePickerProps["onDelete"] = (e) => {
    onDeleteProp?.(e);

    onChange("");
  };

  return (
    <ImagePicker
      ref={mergedRef}
      {...imagePickerProps}
      {...fieldProps}
      setValue={handleChange}
      onDelete={handleDelete}
    >
      <ImagePickerActions />
      <ImagePickerContent />
    </ImagePicker>
  );
}

const FormImagePicker = React.forwardRef(FormImagePickerInner) as <
  TFieldValues extends FieldValues
>(
  props: FormImagePickerProps<TFieldValues> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  }
) => ReturnType<typeof FormImagePickerInner>;

export { FormImagePicker };
